import * as React from "react";
import { debounce, entries } from "lodash";
const { Component } = React;

import Files from "react-files";

import * as UserStore from "../users/UserStore";
import { FormGroup, Label, Input, Row, Col, Button } from "reactstrap";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import * as OrganizationStore from "./OrganizationStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface OrganizationBrandingState {
    organizationId: string;
    isLoading: boolean;
    brandingContent: any;
    schema: any;
    themeKeys: any;
    localizations: any;
    showCreate: boolean;
    imageHashes: number;
    waitingForSave: boolean;
    domains: string[];
    newDomain: string;
}

interface OrganizationBrandingProps {
    organization: string;
}

let contextSubscriptionId = "";

let objectMap = (obj, func) => {
    return entries(obj).map((kv) => {
        let [k, v] = kv;
        return func(k, v);
    });
};

export default class OrganizationBranding extends Component<OrganizationBrandingProps, OrganizationBrandingState> {
    constructor(props) {
        super(props);
        this.state = {
            organizationId: "",
            brandingContent: {},
            isLoading: true,
            schema: {},
            themeKeys: [],
            localizations: [],
            showCreate: false,
            imageHashes: +new Date(),
            waitingForSave: false,
            domains: [],
            newDomain: "",
        };
    }

    componentDidMount() {
        contextSubscriptionId = GetContext((context) => {
            this.GetBranding(context.currentOrganization.id);
        });

        this.debounceUpdate = debounce(function () {
            OrganizationStore.UpdateBranding(this.state.organizationId, this.state.brandingContent).then(() => {
                this.setState({ imageHashes: +new Date(), waitingForSave: false });
            });
        }, 750);
    }

    GetBranding = (organizationId) => {
        this.setState({
            organizationId: "",
            brandingContent: {},
            isLoading: true,
            schema: {},
            themeKeys: [],
            localizations: [],
            showCreate: false,
            domains: [],
        });

        OrganizationStore.GetBranding(organizationId)
            .then((res) => {
                return this.setState({
                    organizationId,
                    brandingContent: res.data,
                    themeKeys: objectMap(res.data.theme, (key, value) => ({ key, value })),
                    localizations: objectMap(res.data.localization, (language, values) => ({ language, values: objectMap(values, (key, value) => ({ key, value })) })),
                    isLoading: false,
                });
            })
            .catch(() => {
                return this.setState({
                    organizationId,
                    isLoading: false,
                    showCreate: true,
                });
            });

        OrganizationStore.GetBrandingDomains(organizationId).then((res) => {
            this.setState({ domains: res.data });
        });
    };

    componentWillUnmount() {
        UnsubscribeContext(contextSubscriptionId);
    }

    handleChange = (value) => {
        this.setState({ brandingContent: value }, this.debounceUpdate);
    };

    debounceUpdate = () => {};

    addNewImage = () => {
        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images.push({ key: "", isRedirectURI: true, redirectURI: "" });

            return { brandingContent };
        }, this.save);
    };

    handleImageKeyChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].key = value;

            return { brandingContent };
        }, this.save);
    };

    handleImageIsRedirectChange = (i) => (e) => {
        let checked = e.target.checked;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].isRedirect = checked;

            return { brandingContent };
        }, this.save);
    };

    handleImageIsNotRedirectChange = (i) => (e) => {
        let checked = !e.target.checked;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].isRedirect = checked;

            return { brandingContent };
        }, this.save);
    };

    handleImageRedirectURIChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].redirectURI = value;

            return { brandingContent };
        }, this.save);
    };

    handleImageMimeChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images[i].mimeType = value;

            return { brandingContent };
        }, this.save);
    };

    handleImageFilesChange = (i) => (files) => {
        var reader = new FileReader();

        reader.onload = (evt: any) => {
            this.setState((prevState) => {
                let { brandingContent } = prevState;

                let dataRegex = /data:((\w|-)+(\/((\w|-)+))+);base64,(.+)/;

                let result = dataRegex.exec(evt.target.result.toString());
                if (result != null) {
                    brandingContent.images[i].content = result[6];
                    brandingContent.images[i].mimeType = result[1];
                }

                return { brandingContent };
            }, this.save);
        };

        reader.readAsDataURL(files[0]);
    };

    removeImage = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { brandingContent } = prevState;

            brandingContent.images.splice(i, 1);

            return { brandingContent };
        }, this.save);
    };

    handleThemeKeyChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys[i].key = value;

            return { themeKeys };
        }, this.save);
    };

    handleThemeValueChange = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys[i].value = value;

            return { themeKeys };
        }, this.save);
    };

    removeThemeKey = (i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys.splice(i, 1);

            return { themeKeys };
        }, this.save);
    };

    handleLocalizationKeyChange = (language, i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[language].values[i].key = value;

            return { localizations };
        }, this.save);
    };

    handleLocalizationValueChange = (language, i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[language].values[i].value = value;

            return { localizations };
        }, this.save);
    };

    removeLocalizationProperty = (language, i) => (e) => {
        let value = e.target.value;

        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[language].values.splice(i, 1);

            return { localizations };
        }, this.save);
    };

    addNewThemeKey = () => {
        this.setState((prevState) => {
            let { themeKeys } = prevState;

            themeKeys.push({ key: "", value: "" });

            return { themeKeys };
        }, this.save);
    };

    addNewLocalizationProperty = (l) => () => {
        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations[l].values.push({ key: "", value: "" });

            return { localizations };
        }, this.save);
    };

    createBranding = () => {
        OrganizationStore.CreateBranding(this.state.organizationId).then(() => this.GetBranding(this.state.organizationId));
    };

    save = () => {
        this.setState(
            (prevState) => {
                let { brandingContent, themeKeys, localizations } = prevState;

                brandingContent.theme = themeKeys.reduce((agg, cur) => {
                    agg[cur.key] = cur.value;
                    return agg;
                }, {});

                brandingContent.localization = localizations.reduce((a, c) => {
                    a[c.language] = c.values.reduce((agg, cur) => {
                        agg[cur.key] = cur.value;
                        return agg;
                    }, {});
                    return a;
                }, {});

                return { brandingContent, waitingForSave: true };
            },
            () => {
                this.debounceUpdate();
            }
        );
    };

    copyLink = (link) => () => {
        var url = window.location.origin + link;
        // @ts-ignore
        navigator.permissions.query({ name: "clipboard-write" }).then((result) => {
            if (result.state == "granted" || result.state == "prompt") {
                navigator.clipboard.writeText(url).then(
                    function () {
                        /* clipboard successfully set */
                    },
                    function (e) {
                        console.log(e);
                        /* clipboard write failed */
                    }
                );
            }
        });
    };

    removeDomain = (domain) => () => {
        OrganizationStore.RemoveDomain(this.state.organizationId, domain).then(() => this.GetBranding(this.state.organizationId));
    };

    handleNewDomainChange = (e) => {
        let value = e.target.value;
        this.setState({ newDomain: value });
    };

    addDomain = () => {
        OrganizationStore.AddDomain(this.state.organizationId, this.state.newDomain).then(() => this.GetBranding(this.state.organizationId));
    };

    addLocaliztion = (language) => () => {
        this.setState((prevState) => {
            let { localizations } = prevState;

            localizations.push({ language: language, values: [] });

            return { localizations };
        }, this.save);
    };

    render() {
        return (
            !this.state.isLoading && (
                <>
                    {this.state.showCreate && (
                        <Button color="primary" onClick={this.createBranding}>
                            Create
                        </Button>
                    )}
                    {!this.state.showCreate && (
                        <>
                            <h4>Domains</h4>
                            <div className="form-group row">
                                <label className="col-sm-3 col-form-label">Domains</label>
                                <div className="col-sm-9 pl-0">
                                    {this.state.domains.map((domain, key) => (
                                        <div key={"domain" + key} className="d-flex justify-content-between">
                                            <span>{domain}</span>
                                            <button type="button" className="btn btn-outline-danger" onClick={this.removeDomain(domain)}>
                                                <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                                <div className="col-12 p-3">
                                    <div className="row">
                                        <div className="col-10">
                                            <input type="text" className="form-control" value={this.state.newDomain} onChange={this.handleNewDomainChange} />
                                        </div>
                                        <div className="col-2 d-flex justify-content-end">
                                            <button type="button" className="btn btn-outline-success" onClick={this.addDomain}>
                                                <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <h4>Images {this.state.waitingForSave && <FontAwesomeIcon icon={["fad", "spinner"]} spin={true}></FontAwesomeIcon>}</h4>
                            <div className="pl-2">
                                <Row>
                                    {this.state.brandingContent.images.map((image, i) => (
                                        <Col sm="12" md="6" lg="4" key={`i${i}`}>
                                            <Row>
                                                <Col style={{ height: "200px" }}>
                                                    <img
                                                        key={`i${i}hash${this.state.imageHashes}}`}
                                                        src={`/static/branding/images/${image.key}?brandingId=${this.state.organizationId}&hash=${this.state.imageHashes}`}
                                                        alt=""
                                                        style={{ maxWidth: "200px", display: "block", margin: "auto" }}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs="12" sm="2">
                                                    <Label for={`i${i}key`}>Key</Label>
                                                </Col>
                                                <Col xs="12" sm="10">
                                                    <Input id={`i${i}key`} type="text" value={image.key} onChange={this.handleImageKeyChange(i)}></Input>
                                                </Col>
                                            </Row>

                                            <FormGroup check inline>
                                                <Input type="radio" radioGroup="imageType" id={`i${i}Image`} checked={!image.isRedirect} onChange={this.handleImageIsNotRedirectChange(i)} />
                                                <Label for={`i${i}Image`}>File</Label>
                                            </FormGroup>
                                            <FormGroup check inline>
                                                <Input type="radio" radioGroup="imageType" id={`i${i}redirect`} checked={image.isRedirect} onChange={this.handleImageIsRedirectChange(i)} />
                                                <Label for={`i${i}redirect`}>URL</Label>
                                            </FormGroup>

                                            {image.isRedirect && (
                                                <FormGroup>
                                                    <Label for={`i${i}url`}>URL</Label>
                                                    <Input id={`i${i}url`} type="text" value={image.redirectURI} onChange={this.handleImageRedirectURIChange(i)}></Input>
                                                </FormGroup>
                                            )}
                                            {!image.isRedirect && (
                                                <>
                                                    <Files className="files-dropzone" onChange={this.handleImageFilesChange(i)} accepts={[".jpeg", ".jpg", ".png"]} maxFileSize={10000000} minFileSize={0} clickable>
                                                        <div className={`p-2 ${image.content ? "text-primary" : ""}`} style={{ cursor: "pointer" }}>
                                                            {image.content ? "Drop or click to update" : "Drop file here or click to upload"}
                                                        </div>
                                                    </Files>
                                                    <FormGroup>
                                                        <Label for={`i${i}mime`}>Mime Type</Label>
                                                        <Input id={`i${i}mime`} type="text" value={image.mimeType} onChange={this.handleImageMimeChange(i)}></Input>
                                                    </FormGroup>
                                                </>
                                            )}

                                            <div className="d-flex justify-content-between">
                                                <Button outline onClick={this.copyLink(`/static/branding/images/${image.key}?brandingId=${this.state.organizationId}`)}>
                                                    <FontAwesomeIcon icon={["fad", "link"]}></FontAwesomeIcon>
                                                </Button>
                                                <Button color="danger" outline onClick={this.removeImage(i)}>
                                                    <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                                </Button>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                                <Button outline color="primary" onClick={this.addNewImage}>
                                    Add New Image
                                </Button>
                            </div>
                            <h4>Theme {this.state.waitingForSave && <FontAwesomeIcon icon={["fad", "spinner"]} spin={true}></FontAwesomeIcon>}</h4>
                            <div className="pl-2">
                                {this.state.themeKeys.map((themeKey, t) => (
                                    <div key={`t${t}`}>
                                        <FormGroup>
                                            <Row>
                                                <Col>
                                                    <Input type="text" value={themeKey.key} onChange={this.handleThemeKeyChange(t)}></Input>
                                                </Col>
                                                <Col>
                                                    <Input type="text" value={themeKey.value} onChange={this.handleThemeValueChange(t)}></Input>
                                                    <Input type="color" value={themeKey.value} onChange={this.handleThemeValueChange(t)}></Input>
                                                </Col>
                                                <Col>
                                                    <Button outline color="danger" onClick={this.removeThemeKey(t)}>
                                                        <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </FormGroup>
                                    </div>
                                ))}
                                <Button outline color="primary" onClick={this.addNewThemeKey}>
                                    Add New Theme Property
                                </Button>
                                <pre>
                                    <code>accent primary-base-color secondary-base-color</code>
                                </pre>
                            </div>
                            <h4>Localizations {this.state.waitingForSave && <FontAwesomeIcon icon={["fad", "spinner"]} spin={true}></FontAwesomeIcon>}</h4>
                            <div className="pl-2">
                                <Button onClick={this.addLocaliztion("en")}>EN</Button>
                                <Button onClick={this.addLocaliztion("af")}>AF</Button>
                                <Button onClick={this.addLocaliztion("ar")}>AR</Button>
                                {this.state.localizations.map((language, l) => (
                                    <div key={`l${l}`}>
                                        <strong>{language.language}</strong>
                                        <div className="pl-2">
                                            {language.values.map((value, v) => (
                                                <div key={`l${l}v${v}`}>
                                                    <FormGroup>
                                                        <Row>
                                                            <Col>
                                                                <Input type="text" value={value.key} onChange={this.handleLocalizationKeyChange(l, v)}></Input>
                                                            </Col>
                                                            <Col>
                                                                <Input type="text" value={value.value} onChange={this.handleLocalizationValueChange(l, v)}></Input>
                                                            </Col>
                                                            <Col>
                                                                <Button outline color="danger" onClick={this.removeLocalizationProperty(l, v)}>
                                                                    <FontAwesomeIcon icon={["fad", "trash"]}></FontAwesomeIcon>
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </FormGroup>
                                                </div>
                                            ))}
                                        </div>
                                        <Button outline color="primary" onClick={this.addNewLocalizationProperty(l)}>
                                            Add New {language.language} Localization Property
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            )
        );
    }
}
